import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { apiClient } from '@/services/api-client';

export type NavigationOrderStatus =
  | 'received'
  | 'paid'
  | 'validated'
  | 'awaiting_review'
  | 'in_progress'
  | 'delivered'
  | 'failed'
  | 'refunded'
  | 'cancelled';

export interface NavigationOrderListItem {
  id: string;
  publicId: string;
  status: NavigationOrderStatus;
  waterName: string;
  waterType: string;
  declarationKind: string;
  contactEmail: string;
  serviceFeeCents: number;
  paidAt: string | null;
  createdAt: string;
  recRegistryNumber: string | null;
  recPresentedAt: string | null;
  resolvedAt: string | null;
  hasDeclarationPdf: boolean;
  hasNotificationsPdf: boolean;
}

interface RpcRow {
  id: string;
  public_id: string;
  status: NavigationOrderStatus;
  water_body_name: string;
  water_type: string;
  declaration_kind: string;
  contact_email: string;
  service_fee_cents: number;
  paid_at: string | null;
  created_at: string;
  rec_registry_number: string | null;
  rec_presented_at: string | null;
  resolved_at: string | null;
  has_declaration_pdf: boolean;
  has_notifications_pdf: boolean;
}

function mapRow(row: RpcRow): NavigationOrderListItem {
  return {
    id: row.id,
    publicId: row.public_id,
    status: row.status,
    waterName: row.water_body_name,
    waterType: row.water_type,
    declarationKind: row.declaration_kind,
    contactEmail: row.contact_email,
    serviceFeeCents: Number(row.service_fee_cents || 0),
    paidAt: row.paid_at,
    createdAt: row.created_at,
    recRegistryNumber: row.rec_registry_number,
    recPresentedAt: row.rec_presented_at,
    resolvedAt: row.resolved_at,
    hasDeclarationPdf: Boolean(row.has_declaration_pdf),
    hasNotificationsPdf: Boolean(row.has_notifications_pdf),
  };
}

export function useNavigationOrdersList(status: string, search: string) {
  return useQuery<NavigationOrderListItem[]>({
    queryKey: ['admin', 'navigation-orders', status, search],
    queryFn: async () => {
      const params: Record<string, unknown> = { p_limit: 200 };
      if (status && status !== 'all') params.p_status = status;
      if (search.trim()) params.p_search = search.trim();
      const { data, error } = await dbClient.rpc<RpcRow>('admin_navigation_orders_list', params);
      if (error) throw new Error(error.message);
      return (normalizeRpcResponse(data) as RpcRow[]).map(mapRow);
    },
    staleTime: 30_000,
  });
}

export function useNavigationOrderAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      orderId: string;
      action: 'present_rec' | 'resolve' | 'fail';
      recRegistryNumber?: string;
      recPresentedAt?: string;
      note?: string;
    }) => {
      const { data, error } = await apiClient.post<{ ok: boolean }>(
        `/ops/navigation-orders/${body.orderId}/action`,
        {
          action: body.action,
          recRegistryNumber: body.recRegistryNumber,
          recPresentedAt: body.recPresentedAt,
          note: body.note,
        },
      );
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'navigation-orders'] });
    },
  });
}
